import { useCallback, useEffect, useState } from 'react'

import { type SessionUser } from '@/platform/access/auth'
import { type EntryLearner } from '@/platform/access/learner-session'
import { type FamilyContext } from '@/platform/family/api'
import { type Learner } from '@/platform/learners/api'
import { toAppError } from '@/shared/errors/envelope'

import { ChildModeScreen } from './screens/ChildModeScreen'
import { LearnerPinScreen } from './screens/entry/LearnerPinScreen'
import { LearnerPinSetupScreen } from './screens/entry/LearnerPinSetupScreen'
import { LearnerSelectScreen } from './screens/entry/LearnerSelectScreen'
import { ParentGateScreen } from './screens/entry/ParentGateScreen'
import { LearnerForm } from './screens/LearnerForm'
import { LoginScreen } from './screens/LoginScreen'
import { type DemoRunReport } from './screens/materials/model'
import { ParentHome } from './screens/ParentHome'
import { SetPinScreen } from './screens/SetPinScreen'
import { type AppServices } from './services'

/**
 * Family entry (FR-01, FR-02, FR-03; D-16).
 *
 * First time: parent login → family bootstrap → parent PIN → first learner →
 * that learner's PIN → learner selection.
 * Every opening afterwards: learner selection (the parent's Supabase session
 * is kept). A learner picks their profile and enters their own PIN; the
 * server starts a learner session. A learner whose session on this device is
 * still live resumes without the PIN. "Bố mẹ" needs the parent PIN.
 *
 * All authorization happens server-side; this component only decides which
 * screen to show. The device keeps at most one learner session; switching
 * learner, leaving child mode and signing out end it on the server.
 */

export interface AppProps {
  readonly services: AppServices
}

interface LearnerRef {
  readonly id: string
  readonly nickname: string
}

type View =
  | { kind: 'loading' }
  | { kind: 'error'; sessionExpired: boolean }
  | { kind: 'set_pin' }
  | { kind: 'select' }
  | { kind: 'learner_pin'; learner: LearnerRef; from: 'select' | 'home' }
  | { kind: 'parent_gate' }
  | { kind: 'home' }
  | { kind: 'create'; first: boolean }
  | { kind: 'edit'; learner: Learner }
  | {
      kind: 'learner_pin_setup'
      learner: LearnerRef
      reset: boolean
      then: 'home' | 'select'
    }
  | { kind: 'child'; learner: LearnerRef; token: string }

function Centered({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
      {children}
    </main>
  )
}

export function App({ services }: AppProps): React.JSX.Element {
  const [user, setUser] = useState<SessionUser | null | undefined>(undefined)
  const [family, setFamily] = useState<FamilyContext | null>(null)
  const [learners, setLearners] = useState<Learner[]>([])
  const [entry, setEntry] = useState<EntryLearner[]>([])
  const [view, setView] = useState<View>({ kind: 'loading' })
  const [reloadKey, setReloadKey] = useState(0)
  // Last DEMO school-material run (Slice 2), for the parent preview. Memory
  // only, never linked to a learner, gone on reload or sign-out.
  const [demoRun, setDemoRun] = useState<DemoRunReport | null>(null)

  useEffect(() => {
    let active = true
    void services.auth.getSessionUser().then((u) => {
      if (active) setUser(u)
    })
    const unsubscribe = services.auth.onSessionChange((u) => {
      setUser(u)
    })
    return () => {
      active = false
      unsubscribe()
    }
  }, [services])

  const userId = user?.id

  // Stable, so Parent Insight does not reload on every render.
  const loadMaterialActivity = useCallback(
    (learnerId: string) => services.materialLearning.activity(learnerId),
    [services],
  )

  const refreshEntry = useCallback(async () => {
    const [list, entryList] = await Promise.all([
      services.learners.listLearners(),
      services.learnerSessions.listEntryLearners(),
    ])
    setLearners(list)
    setEntry(entryList)
    return { list, entryList }
  }, [services])

  /** After the parent PIN exists: learner selection, or the first learner. */
  const enterFamily = useCallback(
    async (isActive: () => boolean) => {
      const { list } = await refreshEntry()
      if (isActive())
        setView(list.length === 0 ? { kind: 'create', first: true } : { kind: 'select' })
    },
    [refreshEntry],
  )

  useEffect(() => {
    if (userId === undefined) return
    let active = true
    const isActive = () => active
    void (async () => {
      try {
        const ctx = await services.family.bootstrapFamily()
        if (!isActive()) return
        setFamily(ctx)
        if (!ctx.pin_configured) {
          setView({ kind: 'set_pin' })
          return
        }
        await enterFamily(isActive)
      } catch (e) {
        if (isActive()) {
          setView({
            kind: 'error',
            sessionExpired: toAppError(e).code === 'SESSION_EXPIRED',
          })
        }
      }
    })()
    return () => {
      active = false
    }
  }, [userId, services, enterFamily, reloadKey])

  if (user === undefined) {
    return (
      <Centered>
        <p className="text-slate-600 dark:text-slate-400">Đang tải…</p>
      </Centered>
    )
  }

  if (user === null) {
    return <LoginScreen onSendLink={(email) => services.auth.sendMagicLink(email)} />
  }

  const uid = user.id

  /** Ends this device's learner session on the server (best effort) and forgets it. */
  const endDeviceSession = async (reason: 'ended' | 'switched') => {
    const stored = services.childMode.load(uid)
    services.childMode.clear(uid)
    if (stored !== null)
      await services.learnerSessions.end(stored.token, reason).catch(() => undefined)
  }

  const signOut = async () => {
    services.childMode.clear(uid)
    // Every learner session of this parent login ends; the sign-out is global.
    await services.learnerSessions.endAllMine().catch(() => undefined)
    await services.auth.signOut()
    setFamily(null)
    setLearners([])
    setEntry([])
    setDemoRun(null)
    setView({ kind: 'loading' })
    setUser(null)
  }

  /** A learner picked their profile: resume this device's live session, or ask the PIN. */
  const pickLearner = async (learner: LearnerRef, from: 'select' | 'home') => {
    const stored = services.childMode.load(uid)
    if (stored !== null && stored.learnerId === learner.id) {
      const ping = await services.learnerSessions
        .ping(stored.token, true)
        .catch(() => null)
      if (ping?.valid === true && ping.learner_id === learner.id) {
        setView({
          kind: 'child',
          learner: { id: learner.id, nickname: ping.nickname },
          token: stored.token,
        })
        return
      }
    }
    if (stored !== null) await endDeviceSession('switched')
    setView({ kind: 'learner_pin', learner, from })
  }

  const afterPinSetup = async (then: 'home' | 'select') => {
    await refreshEntry().catch(() => undefined)
    setView({ kind: then })
  }

  const pinConfigured = new Set(
    entry.filter((e) => e.pin_configured).map((e) => e.learner_id),
  )

  switch (view.kind) {
    case 'loading':
      return (
        <Centered>
          <p className="text-slate-600 dark:text-slate-400">Đang tải…</p>
        </Centered>
      )

    case 'error':
      return (
        <Centered>
          <p className="text-4xl" aria-hidden>
            ⚠️
          </p>
          <p role="alert" className="text-lg">
            {view.sessionExpired
              ? 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.'
              : 'Không tải được dữ liệu. Vui lòng thử lại.'}
          </p>
          <div className="flex gap-3">
            {!view.sessionExpired && (
              <button
                type="button"
                className="h-12 rounded-xl bg-sky-700 px-5 font-semibold text-white"
                onClick={() => {
                  setView({ kind: 'loading' })
                  setReloadKey((k) => k + 1)
                }}
              >
                Thử lại
              </button>
            )}
            <button
              type="button"
              className="h-12 rounded-xl border border-slate-300 px-5 dark:border-slate-700"
              onClick={() => void signOut()}
            >
              Đăng xuất
            </button>
          </div>
        </Centered>
      )

    case 'set_pin':
      return (
        <SetPinScreen
          onSetPin={async (pin) => {
            const result = await services.family.setParentPin(pin)
            if (result.configured !== true) throw new Error('PIN not configured')
            setFamily((f) => (f === null ? f : { ...f, pin_configured: true }))
            setView({ kind: 'loading' })
            await enterFamily(() => true)
          }}
        />
      )

    case 'select':
      return (
        <LearnerSelectScreen
          learners={entry}
          onPick={(learner) => {
            void pickLearner(
              { id: learner.learner_id, nickname: learner.nickname },
              'select',
            )
          }}
          onParent={() => {
            setView({ kind: 'parent_gate' })
          }}
        />
      )

    case 'learner_pin':
      return (
        <LearnerPinScreen
          learner={view.learner}
          onStart={(pin) => services.learnerSessions.start(view.learner.id, pin)}
          onStarted={(session) => {
            services.childMode.save(uid, {
              learnerId: session.learner_id,
              token: session.session_token,
            })
            setView({
              kind: 'child',
              learner: { id: session.learner_id, nickname: session.nickname },
              token: session.session_token,
            })
          }}
          onBack={() => {
            setView({ kind: view.from })
          }}
        />
      )

    case 'parent_gate':
      return (
        <ParentGateScreen
          initialLockedUntil={family?.pin_locked_until ?? null}
          onVerifyPin={(pin) => services.family.verifyParentPin(pin)}
          onUnlocked={() => {
            setView({ kind: 'home' })
          }}
          onBack={() => {
            setView({ kind: 'select' })
          }}
        />
      )

    case 'learner_pin_setup':
      return (
        <LearnerPinSetupScreen
          learner={view.learner}
          reset={view.reset}
          onSave={(newPin, parentPin) =>
            services.learnerSessions.setLearnerPin(view.learner.id, newPin, parentPin)
          }
          onDone={() => void afterPinSetup(view.then)}
          onCancel={() => {
            setView({ kind: view.then === 'select' ? 'home' : view.then })
          }}
          {...(view.then === 'select' ? { cancelLabel: 'Để sau' } : {})}
        />
      )

    case 'create':
      return (
        <LearnerForm
          {...(view.first
            ? {}
            : {
                onCancel: () => {
                  setView({ kind: 'home' })
                },
              })}
          onSubmit={async (profile, levels) => {
            const learner = await services.learners.createLearner(profile, levels)
            setLearners((list) => [...list, learner])
            setEntry((list) => [
              ...list,
              {
                learner_id: learner.id,
                nickname: learner.nickname,
                pin_configured: false,
              },
            ])
            // The new learner needs their own PIN to enter (D-16).
            setView({
              kind: 'learner_pin_setup',
              learner: { id: learner.id, nickname: learner.nickname },
              reset: false,
              then: view.first ? 'select' : 'home',
            })
          }}
        />
      )

    case 'edit':
      return (
        <LearnerForm
          initial={view.learner}
          onCancel={() => {
            setView({ kind: 'home' })
          }}
          onSubmit={async (profile, levels) => {
            const updated = await services.learners.updateLearner(
              view.learner.id,
              profile,
              levels,
            )
            setLearners((list) => list.map((l) => (l.id === updated.id ? updated : l)))
            setEntry((list) =>
              list.map((e) =>
                e.learner_id === updated.id ? { ...e, nickname: updated.nickname } : e,
              ),
            )
            setView({ kind: 'home' })
          }}
        />
      )

    case 'child':
      return (
        <ChildModeScreen
          key={view.learner.id}
          learnerId={view.learner.id}
          nickname={view.learner.nickname}
          sessionToken={view.token}
          learning={services.learning}
          demoMaterials={services.demoMaterials}
          materials={services.materials}
          materialLearning={services.materialLearning}
          readAloud={services.readAloud}
          onDemoRun={setDemoRun}
          initialLockedUntil={family?.pin_locked_until ?? null}
          onVerifyPin={(pin) => services.family.verifyParentPin(pin)}
          onPingSession={(token, active) => services.learnerSessions.ping(token, active)}
          onRestartSession={(pin) => services.learnerSessions.start(view.learner.id, pin)}
          onSessionRenewed={(session) => {
            services.childMode.save(uid, {
              learnerId: session.learner_id,
              token: session.session_token,
            })
            setView({ ...view, token: session.session_token })
          }}
          onSwitchLearner={() => {
            void endDeviceSession('switched').then(() => {
              setView({ kind: 'select' })
            })
          }}
          onExit={() => {
            void endDeviceSession('ended').then(() => {
              setView({ kind: 'home' })
            })
          }}
        />
      )

    case 'home':
      return (
        <ParentHome
          familyName={family?.family_name ?? ''}
          learners={learners}
          demoInsight={services.demoInsight}
          demoMaterialsEnabled={services.demoMaterials !== null}
          demoRun={demoRun}
          pinConfigured={pinConfigured}
          loadMaterialActivity={loadMaterialActivity}
          materialConsent={services.materialConsent}
          onAdd={() => {
            setView({ kind: 'create', first: false })
          }}
          onEdit={(learner) => {
            setView({ kind: 'edit', learner })
          }}
          onSetLearnerPin={(learner) => {
            setView({
              kind: 'learner_pin_setup',
              learner: { id: learner.id, nickname: learner.nickname },
              reset: pinConfigured.has(learner.id),
              then: 'home',
            })
          }}
          onLearnerEntry={() => {
            setView({ kind: 'select' })
          }}
          onEnterChildMode={(learner) => {
            const ref = { id: learner.id, nickname: learner.nickname }
            if (pinConfigured.has(learner.id)) void pickLearner(ref, 'home')
            else
              setView({
                kind: 'learner_pin_setup',
                learner: ref,
                reset: false,
                then: 'home',
              })
          }}
          onSignOut={() => void signOut()}
        />
      )
  }
}
